import React from "react";
import Page from "../../page-layout";
import PromoCategories from "../../../components/PromoCategories";
import Slider from "../../../components/Slider";
import HomePageSkeleton from "./homePageSkeleton";
import MaxWidthContainer from "../../../components/UI/MaxWidthContainer/MaxWidthContainer";
import { useHistory } from "react-router";
import { useGetHomePageQuery } from "../../../providers/page-items";
import { IonIcon } from "@ionic/react";
import { useAuth } from "../../../contexts/auth-context";
import { CategoriesSection, PromoSection, ProductShelvesSection, RecipesSection, SelectionsSection } from "@core/types";
import { useGetUnreadCountQuery } from "../../../providers/notifications";
import {
  AddressSelectionModal,
  Categories,
  ProductShelf,
  ProductsSearchBarHeader,
  Selections,
} from "../../../parhato2/src/entities";
import { useScrollRestoration } from "../../../hooks/useScrollRestoration";
import VpnErrorState from "./VpnErrorState";
import PullToRefresh from "../../../parhato2/src/entities/pullToRefresh";
import { personCircle, notifications, arrowUpCircleOutline } from "ionicons/icons";
import { useConfig } from "../../../providers/config";
import UnpaidOrdersBanner from "../../../components/UnpaidOrdersBanner";
import MobileAppInstallCtaPanel from "../../../components/MobileAppInstallCtaPanel";
import UnreviewedOrdersBanner from "../../../components/UnreviewedOrdersBanner";
import UpdateBottomSheet from "../../../components/UpdateBottomSheet";
import useUpdateNotification from "../../../hooks/useUpdateNotification";
import { Capacitor } from "@capacitor/core";

const HomeParhato2 = () => {
  const config = useConfig();
  const history = useHistory();
  const isNative = Capacitor.isNativePlatform();
  const { isUserAuthenticated, user } = useAuth();
  const { data: fetchedHomePage, isLoading, error, refetch } = useGetHomePageQuery();
  const customerId = user?.id;

  const { data: unreadCountData } = useGetUnreadCountQuery({
    params: { customerId: customerId || "" },
    options: {
      enabled: Boolean(isUserAuthenticated && customerId && config.isNotificationsPageEnabled),
      staleTime: 0,
      cacheTime: 30 * 1000,
    },
  });

  const unreadCount = unreadCountData?.unreadCount || 0;

  const { contentRef, handleScroll } = useScrollRestoration("home-parhato2", !isLoading && !!fetchedHomePage);

  const updateNotification = useUpdateNotification();

  const handleGoToSearch = () => {
    history.push("/search-main-categories");
  };

  const handleGoToProfile = () => {
    if (isUserAuthenticated) {
      history.push("/my-account");
    } else {
      history.push("/auth");
    }
  };

  const handleGoToNotifications = () => {
    history.push("/notifications");
  };
  const hasNewerVersionAndNative = isNative && updateNotification.hasNewerVersion;
  return (
    <Page>
      <Page.Header className="h-16 mt-[env(safe-area-inset-top)]">
        <MaxWidthContainer classList="!mb-[max(env(safe-area-inset-bottom),1rem)] pr-1.5 pl-1 ">
          <ProductsSearchBarHeader showBackButton={false} onClick={handleGoToSearch} tabIndex={-1} />
        </MaxWidthContainer>
      </Page.Header>
      <Page.Content ref={contentRef} scrollEvents onIonScroll={handleScroll}>
        <PullToRefresh />
        {error && !fetchedHomePage && <VpnErrorState onRetry={refetch} />}
        <div className="w-full mx-auto max-w-[1200px]">
          {config.isPaymentsEnabled && config.isUnpaidOrdersBannerEnabled && <UnpaidOrdersBanner />}
          {config.isUnreviewedOrdersBannerEnabled && <UnreviewedOrdersBanner />}
          <MobileAppInstallCtaPanel />
        </div>
        <MaxWidthContainer classList="flex flex-col gap-3 px-2.5 !mb-[max(env(safe-area-inset-bottom),1rem)] lg:grid lg:grid-cols-2 pb-20">
          {hasNewerVersionAndNative && config.isUpdateNotificationEnabled && (
            <UpdateBottomSheet isOpen={updateNotification.isOpen} onDismiss={updateNotification.close} />
          )}
          <div className="flex items-center justify-between lg:col-span-2">
            <AddressSelectionModal />
            <div className="flex items-center gap-1">
              {config.isNotificationsPageEnabled && (
                <div onClick={handleGoToNotifications} className="relative cursor-pointer">
                  <IonIcon icon={notifications} className="w-[2rem] h-[2rem] text-gray-400" />
                  {isUserAuthenticated && unreadCount > 0 && (
                    <div className="absolute -top-0 -right-0 w-3 h-3 bg-red-500 rounded-full border-1 border-white"></div>
                  )}
                </div>
              )}
              {hasNewerVersionAndNative && (
                <div onClick={updateNotification.open} className="relative cursor-pointer">
                  <IonIcon icon={arrowUpCircleOutline} className="w-[2rem] h-[2rem] text-green-600" />
                </div>
              )}
              <div onClick={handleGoToProfile}>
                <IonIcon icon={personCircle} className="w-9 h-9 text-gray-400" />
              </div>
            </div>
          </div>

          {isLoading && <HomePageSkeleton />}

          {!isLoading &&
            fetchedHomePage?.map((page) => {
              switch (page?.type) {
                case "promos":
                  const promoSection = page as PromoSection;
                  return <PromoCategories key={promoSection.id} {...promoSection} />;
                case "slider":
                  const sliderSection = page as PromoSection;

                  return <Slider key={sliderSection.id} {...sliderSection} />;
                case "product_shelves":
                  const productShelfSection = page as ProductShelvesSection;
                  if (!productShelfSection?.id || productShelfSection?.items?.length === 0) {
                    return <React.Fragment key={productShelfSection?.id}></React.Fragment>;
                  }
                  return <ProductShelf key={productShelfSection.id} {...productShelfSection} />;
                case "selections":
                  const selectionsSection = page as SelectionsSection;
                  return <Selections key={selectionsSection.id} {...selectionsSection} />;
                case "categories":
                  const categoriesSection = page as CategoriesSection;
                  return <Categories {...categoriesSection} key={categoriesSection.id} />;
                case "recipes":
                  const recipesSection = page as RecipesSection;
                  return <div key={recipesSection.id} className="hidden" />;
                default:
                  return <React.Fragment key={page?.id}></React.Fragment>;
              }
            })}
        </MaxWidthContainer>
      </Page.Content>
    </Page>
  );
};

export default HomeParhato2;
